import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useLocation } from "react-router-dom";
import { getUser, logout } from "../../services/auth";
import Icon from "../Icon";
import Avatar from "../Avatar";

// Logo com o badge do módulo (SUPPLY), em snow — feito para o fundo escuro da
// sidebar. Diferente das outras variantes, traz o wordmark e o badge empilhados,
// então a proporção é ~2.9:1 em vez de ~6.2:1 (ver .sidebar-brand img).
import logoSupplier from "../../assets/images/svgs/logo-supplier.svg";

export const SidebarItem = ({item, onNavigate}) => {
    const [active, setActive] = useState(false);

    const location = useLocation();
    const pathname = location.pathname;

    useEffect(() => {
        const itemIsActive = pathname === item.to || (item.to && item.to !== '/' && pathname.startsWith(item.to + '/'));
        const subitemIsActive = item.subitems?.some(subitem => subitem.to === pathname || pathname.startsWith(subitem.to + '/'));

        setActive(itemIsActive || subitemIsActive);
    }, [location]);

    return (
        <li className={active ? 'active' : ''}>
            {!item.subitems && item.external &&
                <a href={item.to} target="_blank" rel="noopener noreferrer" onClick={onNavigate}>
                    <Icon name={item.svg} />
                    {item.name}
                </a>
            }

            {!item.subitems && !item.external &&
                <Link to={item.to} onClick={onNavigate}>
                    <Icon name={item.svg} />
                    {item.name}
                    {item?.alert !== undefined && item?.alert !== 0 &&
                        <span className="alert">{item.alert}</span>
                    }
                </Link>
            }

            {item.subitems &&
                <a onClick={() => setActive(prev => !prev)}>
                    <Icon name={item.svg} />
                    {item.name}
                    <Icon name="arrow" className="arrow" />
                </a>
            }

            {item.subitems &&
                <ul>
                    {item.subitems.map((subitem, index) => {
                        if(subitem.show){
                            return (
                                <SidebarSubitem subitem={subitem} key={index} onNavigate={onNavigate} />
                            );
                        }
                    })}
                </ul>
            }
        </li>
    );
}

export const SidebarSubitem = ({subitem, onNavigate}) => {
    const [active, setActive] = useState(false);

    const location = useLocation();

    useEffect(() => {
        setActive(location.pathname === subitem.to || location.pathname.startsWith(subitem.to + '/'));
    }, [location.pathname, subitem.to]);

    return (
        <li className={active ? 'active' : ''}>
            <Link to={subitem.to} onClick={onNavigate}>
                <span className="dot"></span>
                {subitem.name}
            </Link>
        </li>
    );
}


export const Sidebar = ({mobileOpen = false, onClose}) => {
    const navigate = useNavigate();

    const user = getUser();

    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const userRef = useRef(null);
    const menuRef = useRef(null);

    // Alimenta o --menu-fade do SCSS: altura do fade na base do menu quando há
    // itens abaixo da dobra, encolhendo até 0 conforme o scroll se aproxima do
    // fim (por isso valor contínuo em vez de classe liga/desliga).
    useEffect(() => {
        const menu = menuRef.current;
        if (!menu) return;

        const updateFade = () => {
            const remaining = menu.scrollHeight - menu.scrollTop - menu.clientHeight;
            menu.style.setProperty('--menu-fade', `${Math.max(0, Math.min(32, remaining))}px`);
        };

        updateFade();
        menu.addEventListener('scroll', updateFade, { passive: true });

        // Recalcula quando a viewport muda ou um submenu expande/colapsa.
        const observer = new ResizeObserver(updateFade);
        observer.observe(menu);
        if (menu.firstElementChild) observer.observe(menu.firstElementChild);

        return () => {
            menu.removeEventListener('scroll', updateFade);
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = e => {
            if (userRef.current && !userRef.current.contains(e.target)) {
                setUserMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const signOut = () => {
        logout();
        navigate('/login');
    };

    const getItems = () => {
        const base = [
            {
                name: 'Produtos',
                svg: 'product',
                show: true,
                subitems: [
                    {
                        name: 'Produtos',
                        to: '/product/list',
                        show: true
                    },
                    {
                        name: 'Categorias',
                        to: '/category/list',
                        show: true
                    },
                    {
                        name: 'Cores',
                        to: '/color/list',
                        show: true
                    }
                ]
            },
            {
                name: 'API',
                svg: 'api',
                to: '/api',
                show: true
            },
            {
                name: 'Usuários',
                svg: 'people',
                to: '/user/list',
                show: true
            }
        ];

        return base;
    }

    const items = getItems();

    return (
        <aside id="sidebar" className={mobileOpen ? 'mobile-open' : ''}>
            <button
                type="button"
                className="sidebar-close-button"
                aria-label="Fechar menu"
                onClick={onClose}
            >
                <Icon name="close" size={20} />
            </button>
            <div className="sidebar-brand">
                <Link to="/" onClick={onClose} aria-label="Ir para o início">
                    <img src={logoSupplier} alt="Brinde.me Supply" />
                </Link>
            </div>
            <div id="menu" ref={menuRef}>
                <ul>
                    {items.map((item, index) => {
                        if(item.show){
                            return (
                                <SidebarItem item={item} key={index} onNavigate={onClose} />
                            );
                        }
                    })}
                </ul>
            </div>
            <div id="user" ref={userRef}>
                <button
                    type="button"
                    className="user-identity"
                    onClick={() => setUserMenuOpen(prev => !prev)}
                >
                    <Avatar name={user?.name} size={40} />
                    <div className="user-texts">
                        <p className="name">{user?.name}</p>
                        <p className="role">Fornecedor</p>
                    </div>
                    <Icon name="arrow-dropdown" className={`chevron${userMenuOpen ? ' open' : ''}`} size={16} />
                </button>

                {userMenuOpen &&
                    <div className="user-menu">
                        <div className="user-menu-info">
                            <p className="name">{user?.name}</p>
                            <p className="role">{user?.email}</p>
                        </div>
                        <ul>
                            <li>
                                <button type="button" onClick={signOut}>
                                    <Icon name="logout" size={18} /> Sair
                                </button>
                            </li>
                        </ul>
                    </div>
                }
            </div>
        </aside>
    );
}

export default Sidebar;
